
import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { MessageCircle, User, Search, Users, Settings } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { useNotifications } from '@/hooks/useNotifications';
import { useConnectionRequests, useNetworkData } from '@/contexts/NetworkDataContext';
import { useMessagesData } from '@/contexts/MessagesDataContext';  // 🚀 V166.40.9: Use MessagesDataContext for totalUnreadCount
import { hapticFeedback } from '@/utils/hapticFeedback';
import PreloadingNavLink from './PreloadingNavLink';
import { MAIN_NAV_ITEMS } from '@/config/navigation';

interface BottomNavigationProps {
  chatCount: number;
}

const BottomNavigationComponent: React.FC<BottomNavigationProps> = ({
  chatCount
}) => {
  const location = useLocation();
  const { t } = useTranslation();
  const { user } = useAuth() as { user: any };

  // V163.7: Use cached connection requests from NetworkDataContext
  const connectionRequests = useConnectionRequests();
  const pendingRequestsCount = connectionRequests.length;
  const { hasUnseenNetworkAlerts = false, networkShellVisibleTab } = useNetworkData();
  const isViewingNetworkAlertsTab =
    location.pathname === '/network' && networkShellVisibleTab === 'alerts';
  const shouldPulseNetworkBadge =
    pendingRequestsCount > 0 && hasUnseenNetworkAlerts && !isViewingNetworkAlertsTab;

  // 🚀 V166.40.9: Use MessagesDataContext for real-time unread count
  // Narrow MessagesDataContext type locally to avoid '{}' inference issues
  const { totalUnreadCount } = useMessagesData() as { totalUnreadCount: number };

  // If no authenticated user (e.g., /auth routes), do not render heavy nav or call provider-dependent hooks
  if (!user) {
    return null;
  }
  
  // Use totalUnreadCount from context for badge display
  const badgeCount = totalUnreadCount || 0;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 bg-white dark:bg-card border-t border-[#E8E0DA] dark:border-border z-40 md:hidden"
      data-bottom-nav="true"
      style={{
        zIndex: 40,
        minHeight: 'calc(60px + env(safe-area-inset-bottom, 0px))',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)'
      }}
    >
      <div className="flex justify-around py-3 px-2">
        {MAIN_NAV_ITEMS.map(({ path, labelKey, icon: Icon }) => {
          const isNetwork = path === '/network';
          const isChat = path === '/chat';
          const label = t(labelKey);

          let tabAriaLabel: string = label;
          if (isNetwork && pendingRequestsCount > 0) {
            tabAriaLabel = t('nav.networkPending', { count: pendingRequestsCount, label });
          } else if (isChat && badgeCount > 0) {
            tabAriaLabel = t('nav.chatUnread', { count: badgeCount, label });
          }

          return (
            <PreloadingNavLink
              key={path}
              to={path}
              aria-label={tabAriaLabel}
              className={({ isActive }) =>
                `flex flex-col items-center py-3 px-4 rounded-lg min-h-[48px] min-w-[48px] mobile-nav-item mobile-focus ${
                  isActive
                    ? 'text-[#7C3AED] dark:text-primary bg-[#7C3AED]/10 dark:bg-primary/10 mobile-tab-active mobile-tab-select'
                    : 'text-[#9B9490] dark:text-muted-foreground hover:text-[#7C3AED] dark:hover:text-primary hover:bg-muted'
                }`
              }
              onClick={() => hapticFeedback.tabSelect()}
              style={{
                touchAction: 'manipulation',
                WebkitTapHighlightColor: 'transparent'
              }}
            >
              <div className={isNetwork || isChat ? 'relative' : undefined}>
                {/* Icon is decorative — label is on the parent link */}
                <Icon className="w-6 h-6" aria-hidden="true" focusable={false as unknown as boolean} />
                {isNetwork && pendingRequestsCount > 0 && (
                  <span
                    className={
                      `absolute -top-2 end-[-0.5rem] bg-orange-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold shadow-lg ` +
                      (shouldPulseNetworkBadge ? 'mobile-badge-bounce mobile-badge-pulse-glow' : '')
                    }
                    aria-hidden="true"
                  >
                    {pendingRequestsCount > 9 ? '9+' : pendingRequestsCount}
                  </span>
                )}
                {isChat && badgeCount > 0 && (
                  <span
                    className="absolute -top-2 end-[-0.5rem] text-white text-xs rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 font-bold shadow-lg mobile-badge-bounce mobile-badge-pulse-glow"
                    aria-hidden="true"
                    style={{
                      background: 'linear-gradient(135deg, #4169E1 0%, #8B5CF6 100%)',
                      boxShadow: '0 2px 4px rgba(65, 105, 225, 0.3)'
                    }}
                  >
                    {badgeCount > 99 ? '99+' : badgeCount}
                  </span>
                )}
              </div>
              {/* Visible label is hidden from AT since aria-label on the link covers it */}
              <span className="text-xs mt-1 font-medium" aria-hidden="true">{label}</span>
            </PreloadingNavLink>
          );
        })}
      </div>
    </div>
  );
};

// Performance: Memoize BottomNavigation to prevent unnecessary re-renders
const BottomNavigation = BottomNavigationComponent;

export default BottomNavigation;

